import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowRight } from 'lucide-react';
import { equipmentCategories } from '../data/mock';
import Breadcrumbs from '../components/Breadcrumbs';
import EquipmentFilterSidebar from '../components/EquipmentFilterSidebar';
import { useEquipmentFilters } from '../hooks/useEquipmentFilters';
import { applyFilters, hasActiveFilters } from '../lib/equipmentFilters';

const EquipmentLanding = ({ onQuoteClick }) => {
  const navigate = useNavigate();
  const { filters, toQueryString, ...handlers } = useEquipmentFilters();
  const [showAllItems, setShowAllItems] = useState(false);

  // Reset "Load More" whenever filters change
  useEffect(() => {
    setShowAllItems(false);
  }, [
    filters.searchQuery,
    filters.selectedCategory,
    filters.selectedEquipmentType,
    filters.selectedRange,
    filters.selectedPounds,
    filters.maxPrice,
  ]);

  const allFilteredEquipment = applyFilters(equipmentCategories, filters);
  const filtersActive = hasActiveFilters(filters);

  // For "All" category with no filters, show 6 featured items first
  const shouldLimitItems =
    filters.selectedCategory === 'All' && !showAllItems && !filtersActive;
  const displayedEquipment = shouldLimitItems
    ? allFilteredEquipment.filter((item) => item.featured).slice(0, 6)
    : allFilteredEquipment;

  const showLoadMoreButton =
    filters.selectedCategory === 'All' &&
    !showAllItems &&
    allFilteredEquipment.length > 6 &&
    !filtersActive;

  const goToDetail = (id) => {
    navigate(`/equipment/${id}${toQueryString()}`);
  };

  return (
    <div className="bg-white min-h-screen" data-testid="equipment-landing-page">
      {/* Hero Section */}
      <section className="relative text-white overflow-hidden min-h-[300px] md:min-h-[350px] flex items-center">
        <div className="absolute inset-0 w-full h-full">
          <img
            src="/equipment-hero.jpg"
            alt="Equipment Fleet"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1a2332]/95 via-[#1a2332]/85 to-[#1a2332]/70"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-[#1a2332]/90 via-transparent to-[#1a2332]/50"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16 w-full">
          <div className="max-w-4xl">
            <h1
              className="text-4xl md:text-5xl lg:text-6xl font-black leading-tight"
              style={{ textShadow: '0 4px 30px rgba(0, 0, 0, 0.8)' }}
              data-testid="equipment-hero-title"
            >
              Browse Our Equipment
            </h1>
          </div>
        </div>
      </section>

      <Breadcrumbs />

      {/* Stats Bar */}
      <section className="bg-[#E28724] text-white py-6 md:py-8 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 text-center">
            <div>
              <div className="text-3xl md:text-4xl font-black mb-1">600+</div>
              <div className="text-sm md:text-base text-orange-100 font-medium">Units in Our Fleet</div>
            </div>
            <div>
              <div className="text-3xl md:text-4xl font-black mb-1">25+</div>
              <div className="text-sm md:text-base text-orange-100 font-medium">Years in Business</div>
            </div>
            <div>
              <div className="text-3xl md:text-4xl font-black mb-1">100%</div>
              <div className="text-sm md:text-base text-orange-100 font-medium leading-tight">Final Pricing. No Add-ons. No surprises.</div>
            </div>
            <div>
              <div className="text-3xl md:text-4xl font-black mb-1">On-Time</div>
              <div className="text-sm md:text-base text-orange-100 font-medium">Jobsite Delivery</div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Shop by Equipment Type
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Find the perfect equipment for your project. All pricing includes delivery, pickup, and our industry-low damage waiver.
            </p>
          </div>

          <div className="flex flex-col lg:flex-row gap-8">
            <div className="lg:w-64 flex-shrink-0">
              <EquipmentFilterSidebar filters={filters} handlers={handlers} />
            </div>

            <div className="flex-1">
              {displayedEquipment.length === 0 ? (
                <div className="text-center py-16 px-4" data-testid="no-results-message">
                  <div className="max-w-md mx-auto bg-gray-50 border-2 border-gray-200 rounded-lg p-8">
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">No Results Found</h3>
                    <p className="text-lg text-gray-700 mb-6">
                      But our team can potentially get the equipment for you
                    </p>
                    <Button
                      onClick={onQuoteClick}
                      className="bg-[#E28724] hover:bg-orange-600 text-white px-8 py-3 text-lg font-semibold"
                      data-testid="no-results-quote-button"
                    >
                      Request Quote
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
                  {displayedEquipment.map((equipment) => (
                    <Card
                      key={equipment.id}
                      className="overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border-2 hover:border-[#E28724] cursor-pointer flex flex-col"
                      onClick={() => goToDetail(equipment.id)}
                      data-testid={`equipment-card-${equipment.id}`}
                    >
                      <div className="relative h-72 overflow-hidden bg-white">
                        <img
                          src={equipment.image}
                          alt={equipment.name}
                          className="w-full h-full object-contain p-3 transition-transform duration-300 hover:scale-105"
                        />
                        <div className="absolute bottom-0 left-0 right-0 p-6 text-white bg-gradient-to-t from-black/70 via-black/40 to-transparent">
                          <h3 className="text-2xl font-bold mb-2">{equipment.name}</h3>
                          {equipment.pricing && (
                            <p className="text-orange-300 font-semibold">
                              Starting at ${equipment.pricing.daily}/day
                            </p>
                          )}
                        </div>
                      </div>

                      <CardContent className="p-6 flex flex-col flex-grow">
                        {(() => {
                          const specs = equipment.brands?.[0]?.specs;
                          if (!specs) return null;
                          // Pick 3 most relevant spec fields for the card
                          const priorityKeys = [
                            'Work Height', 'Working Height', 'Platform Height',
                            'Lift Capacity', 'Max Lift Height',
                            'Horizontal Outreach', 'Max Forward Reach',
                            'Platform Capacity', 'Operating Weight', 'Power',
                          ];
                          const entries = [];
                          for (const key of priorityKeys) {
                            if (specs[key] && entries.length < 3) {
                              entries.push([key, specs[key]]);
                            }
                          }
                          return (
                            <ul className="mb-4 space-y-1.5" data-testid={`equipment-specs-${equipment.id}`}>
                              {entries.map(([k, v]) => (
                                <li key={k} className="flex justify-between text-sm border-b border-gray-100 pb-1.5">
                                  <span className="text-gray-500 font-medium">{k}</span>
                                  <span className="text-[#1a2332] font-semibold text-right">{v}</span>
                                </li>
                              ))}
                            </ul>
                          );
                        })()}

                        {equipment.pricing && (
                          <div className="grid grid-cols-3 gap-2 mb-4 text-center">
                            <div>
                              <div className="text-xs text-gray-500 mb-1">Daily</div>
                              <div className="text-lg font-bold text-[#E28724]">${equipment.pricing.daily}</div>
                            </div>
                            <div>
                              <div className="text-xs text-gray-500 mb-1">Weekly</div>
                              <div className="text-lg font-bold text-[#E28724]">${equipment.pricing.weekly}</div>
                            </div>
                            <div>
                              <div className="text-xs text-gray-500 mb-1">Monthly</div>
                              <div className="text-lg font-bold text-[#E28724]">${equipment.pricing.monthly}</div>
                            </div>
                          </div>
                        )}

                        <Button
                          className="w-full bg-[#E28724] hover:bg-orange-600 text-white font-semibold group mt-auto"
                          onClick={(e) => {
                            e.stopPropagation();
                            goToDetail(equipment.id);
                          }}
                          data-testid={`equipment-view-button-${equipment.id}`}
                        >
                          View Equipment
                          <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </Button>
                      </CardContent>
                    </Card>
                  ))}

                  {filters.selectedCategory !== 'All' && (
                    <Card
                      className="overflow-hidden border-2 border-dashed border-gray-300 hover:border-[#E28724] transition-all duration-300"
                      data-testid="cant-find-card"
                    >
                      <CardContent className="p-8 flex flex-col items-center justify-center h-full min-h-[400px] text-center">
                        <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-6">
                          <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                        <h3 className="text-2xl font-bold text-gray-900 mb-3">
                          Can't Find What You're Looking For?
                        </h3>
                        <p className="text-gray-600 mb-6 max-w-sm">
                          Our team can help you source specialized equipment for your project
                        </p>
                        <Button
                          onClick={onQuoteClick}
                          size="lg"
                          className="bg-[#E28724] hover:bg-orange-600 text-white px-8 py-3 font-semibold"
                          data-testid="cant-find-quote-button"
                        >
                          Request Quote
                        </Button>
                      </CardContent>
                    </Card>
                  )}
                </div>
              )}

              {showLoadMoreButton && (
                <div className="text-center mt-12">
                  <Button
                    onClick={() => setShowAllItems(true)}
                    size="lg"
                    className="bg-[#E28724] hover:bg-orange-600 text-white px-12 py-6 text-lg font-semibold"
                    data-testid="load-more-button"
                  >
                    Load More Equipment
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EquipmentLanding;
